import { queryOptions } from "@tanstack/react-query";

import { OperatorsForbiddenError, operatorsQueryKeys } from "../../lib/operatorsReactQuery";
import { getOperatorsHttpOrigin } from "../../operators";
import {
  readOperatorsAuthErrorMessage,
  throwOperatorsAuthExpired,
} from "../../operatorsAuthSession";
import {
  type RespiteNeedsAttentionResponse,
  type RespiteParams,
  type RespiteSummaryResponse,
  isRespiteNeedsAttentionResponse,
  isRespiteSummaryResponse,
} from "./respiteTypes";

type RespiteFilterParams = Pick<RespiteParams, "clinic" | "navigator" | "provider">;

export const respiteQueryKeys = {
  all: [...operatorsQueryKeys.all, "dashboards", "respite"] as const,
  summary: (params: RespiteParams) => [...respiteQueryKeys.all, "summary", params] as const,
  needsAttention: (params: RespiteFilterParams) =>
    [...respiteQueryKeys.all, "needs-attention", params] as const,
};

async function fetchRespiteJson(path: string, search: URLSearchParams, label: string) {
  const response = await fetch(
    `${getOperatorsHttpOrigin()}/api/v1/dashboards/respite/${path}?${search}`,
    { credentials: "include" },
  );
  if (response.status === 401) throwOperatorsAuthExpired();
  if (response.status === 403) {
    const fallback = "You do not have access to Dashboards.";
    const message = await readOperatorsAuthErrorMessage(response).catch(() => fallback);
    throw new OperatorsForbiddenError(message || fallback);
  }
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(() => `Failed to load Respite ${label}`),
    );
  }
  return (await response.json()) as unknown;
}

function filterSearch(params: RespiteFilterParams): URLSearchParams {
  const search = new URLSearchParams();
  if (params.clinic) search.set("clinic", params.clinic);
  if (params.navigator) search.set("navigator", params.navigator);
  if (params.provider) search.set("provider", params.provider);
  return search;
}

export async function fetchRespiteSummary(params: RespiteParams): Promise<RespiteSummaryResponse> {
  const search = filterSearch(params);
  search.set("dateStart", params.dateStart);
  search.set("dateEnd", params.dateEnd);
  const body = await fetchRespiteJson("summary", search, "summary");
  if (!isRespiteSummaryResponse(body)) throw new Error("Respite summary response was malformed");
  return body;
}

export async function fetchRespiteNeedsAttention(
  params: RespiteFilterParams,
): Promise<RespiteNeedsAttentionResponse> {
  const body = await fetchRespiteJson("needs-attention", filterSearch(params), "needs attention");
  if (!isRespiteNeedsAttentionResponse(body)) {
    throw new Error("Respite needs-attention response was malformed");
  }
  return body;
}

export function respiteSummaryQueryOptions(params: RespiteParams) {
  return queryOptions({
    queryKey: respiteQueryKeys.summary(params),
    queryFn: () => fetchRespiteSummary(params),
  });
}

export function respiteNeedsAttentionQueryOptions(params: RespiteFilterParams) {
  return queryOptions({
    queryKey: respiteQueryKeys.needsAttention(params),
    queryFn: () => fetchRespiteNeedsAttention(params),
  });
}
